import ActionConfirmationDialog from '../../../components/ActionConfirmationDialog';
import ReservationReplyDialog from './ReservationReplyDialog';
import { Box, Button, Center, Flex, Link, ProgressCircle, Stack, Table, Text } from '@chakra-ui/react';
import { FiTrash2 } from 'react-icons/fi';
import { InfoTip } from '../../../components/ui/toogle-tip';
import { Link as RouterLink } from 'react-router-dom';
import { reservationDelete } from '../../../operations/admin/reservationDelete';
import { setDocumentTitle } from '../../../utils/documentTitle';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDatabase } from '../../../hooks/useDatabase';
import { useReservationsExtended } from '../../../hooks/useReservationsData';
import { VscReply } from 'react-icons/vsc';

type SortColumn = 'timestamp' | 'code' | 'time' | 'teacher' | 'student' | 'client' | 'location';

type SortValue = string | number | undefined;

type ReservationColumn = {
	key: SortColumn | 'school' | 'level' | 'grade' | 'notes' | 'actions';
	label: string;
	sortable?: boolean;
};

type ReservationItem = {
	id: string;
	reservation: any;
	lesson: any;
	slot: any;
	client: any;
};

type ReservationGroup = {
	id: string;
	items: ReservationItem[];
};

const columns: ReservationColumn[] = [
	{ key: 'timestamp', label: 'Dodano', sortable: true },
	{ key: 'client', label: 'Klient', sortable: true },
	{ key: 'code', label: 'Kod', sortable: true },
	{ key: 'time', label: 'Czas', sortable: true },
	{ key: 'teacher', label: 'Korepetytor', sortable: true },
	{ key: 'location', label: 'Miejsce', sortable: true },
	{ key: 'student', label: 'Uczestnik', sortable: true },
	{ key: 'school', label: 'Szkoła' },
	{ key: 'level', label: 'Poziom' },
	{ key: 'grade', label: 'Klasa' },
	{ key: 'actions', label: '' },
	{ key: 'notes', label: 'Uwagi' },
];

const ReservationsPage = () => {
	useEffect(setDocumentTitle('Rezerwacje')); // eslint-disable-line react-hooks/exhaustive-deps

	const database = useDatabase();
	const { reservationsExtended, loading, error, isReady } = useReservationsExtended();
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
	const [sortColumn, setSortColumn] = useState<SortColumn>('timestamp');
	const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);
	const [deletePending, setDeletePending] = useState(false);
	const [replyGroupId, setReplyGroupId] = useState<string | null>(null);

	const handleSort = (column: SortColumn) => {
		if (column === sortColumn) {
			setSortOrder((previous) => (previous === 'asc' ? 'desc' : 'asc'));
			return;
		}

		setSortColumn(column);
		setSortOrder('asc');
	};

	const getSortValue = (
		reservation: any,
		lesson: any,
		slot: any,
		client: any,
		column: SortColumn,
	): SortValue => {
		switch (column) {
			case 'timestamp':
				return reservation?.timestamp ?? 0;
			case 'code':
				return slot?.code ?? '';
			case 'time':
				return slot ? `${slot.day ?? ''} ${slot.time ?? ''}` : '';
			case 'teacher':
				return slot?.defaultTeacher ?? '';
			case 'student':
				return lesson?.student?.name ?? '';
			case 'client':
				return client?.name ?? reservation?.email ?? '';
			case 'location':
				return slot?.location ?? '';
			default:
				return '';
		}
	};

	const renderItemList = (items: ReservationItem[], renderValue: (item: ReservationItem) => any) => {
		const values = items
			.map((item) => renderValue(item))
			.filter((value) => value !== '' && value !== null && value !== undefined);

		if (!values.length) {
			return '';
		}

		if (values.length === 1) {
			return values[0];
		}

		return (
			<Stack gap={1}>
				{values.map((value, index) => (
					<Box key={`${items[index]?.id ?? 'value'}-${index}`}>{value}</Box>
				))}
			</Stack>
		);
	};

	const getGroupSortValue = useCallback((group: ReservationGroup, column: SortColumn): SortValue => {
		const firstItem = group.items[0];
		if (!firstItem) {
			return '';
		}

		return getSortValue(firstItem.reservation, firstItem.lesson, firstItem.slot, firstItem.client, column);
	}, []);

	const groupedRows = useMemo(() => {
		if (!reservationsExtended.length) {
			return [] as ReservationGroup[];
		}

		const groups = new Map<string, ReservationGroup>();

		reservationsExtended.forEach((row) => {
			const groupId = row.reservation?.id ?? row.reservation?.uid ?? row.lesson?.slot ?? row.client?.uid ?? 'unknown';
			const currentGroup = groups.get(groupId) ?? { id: groupId, items: [] };

			currentGroup.items.push({
				id: row.lesson?.slot ?? `${groupId}-${row.reservation?.uid ?? 'slot'}`,
				reservation: row.reservation,
				lesson: row.lesson,
				slot: row.slot,
				client: row.client,
			});

			groups.set(groupId, currentGroup);
		});

		return [...groups.values()].sort((groupA, groupB) => {
			const left = getGroupSortValue(groupA, sortColumn);
			const right = getGroupSortValue(groupB, sortColumn);

			if (left == null && right == null) return 0;
			if (left == null) return 1;
			if (right == null) return -1;

			if (typeof left === 'number' && typeof right === 'number') {
				return sortOrder === 'asc' ? left - right : right - left;
			}

			const comparison = String(left).localeCompare(String(right), 'pl', { sensitivity: 'base' });
			return sortOrder === 'asc' ? comparison : -comparison;
		});
	}, [reservationsExtended, getGroupSortValue, sortColumn, sortOrder]);

	const renderCell = (columnKey: ReservationColumn['key'], group: ReservationGroup) => {
		const firstItem = group.items[0];
		const items = group.items;

		switch (columnKey) {
			case 'timestamp':
				return firstItem ? new Date(firstItem.reservation?.timestamp).toLocaleString('pl') : '';
			case 'code':
				return renderItemList(items, (item) => {
					const code = item.slot?.code ?? '';
					if (!code) {
						return '';
					}

					return item.slot?.id ? (
						<Link as={RouterLink} to={`/admin/terminy/${item.slot.id}`} display="inline-flex" alignItems="center" _hover={{ color: 'orange.600' }} aria-label={`Otwórz termin ${code}`}>
							<Flex align="center" gap={1.5}>
								<Text textDecorationLine="underline">{code}</Text>
							</Flex>
						</Link>
					) : null;
				});
			case 'time':
				return renderItemList(items, (item) => {
					if (!item.slot) return '';
					return `${item.slot.day ?? ''} ${item.slot.time ?? ''} (${item.slot.duration ?? 0}min)`;
				});
			case 'location':
				return renderItemList(items, (item) => item.slot?.location ?? '');
			case 'teacher':
				return renderItemList(items, (item) => item.slot?.defaultTeacher ?? '');
			case 'student':
				return renderItemList(items, (item) => item.lesson?.student?.name ?? '');
			case 'client':
				{
					return (
						<Link as={RouterLink} to={`/admin/klienci/${firstItem?.client?.id}`} display="inline-flex" alignItems="center" _hover={{ color: 'orange.600' }} aria-label={`Otwórz klienta ${firstItem?.client?.name ?? ''}`}>
							<Flex align="center" gap={1.5}>
								<Text textDecorationLine="underline">{firstItem?.client?.name ?? firstItem?.reservation?.email ?? ''}</Text>
							</Flex>
						</Link>
					);
				}
			case 'school':
				return renderItemList(items, (item) => item.lesson?.student?.school ?? '');
			case 'level':
				return renderItemList(items, (item) => item.lesson?.student?.level ?? '');
			case 'grade':
				return renderItemList(items, (item) => item.lesson?.student?.grade ?? '');
			case 'notes': {
				const seenReservationNotes = new Set<string>();
				const noteValues = items
					.map((item) => {
						const lessonNote = item.lesson?.notes;
						const slotCode = item.slot?.code;
						const reservationNote = item.reservation?.notes;
						if (!lessonNote && !reservationNote) {
							return null;
						}

						const lineItems: React.ReactNode[] = [];

						if (lessonNote) {
							lineItems.push(
								<Flex key={`${item.id}-lesson-note`} align="center" gap={1}>
									<span>{lessonNote}</span>
									{slotCode ? <InfoTip content={`Uwaga do ${slotCode}`} positioning={{ placement: 'right' }} /> : null}
								</Flex>,
							);
						}

						if (reservationNote) {
							const normalizedReservationNote = String(reservationNote).trim();
							if (normalizedReservationNote && !seenReservationNotes.has(normalizedReservationNote)) {
								seenReservationNotes.add(normalizedReservationNote);
								lineItems.push(
									<Box key={`${item.id}-reservation-note`}>{reservationNote}</Box>,
								);
							}
						}

						return lineItems.length ? <Stack gap={1}>{lineItems}</Stack> : null;
					})
					.filter(Boolean);

				if (!noteValues.length) {
					return '';
				}

				if (noteValues.length === 1) {
					return noteValues[0];
				}

				return <Stack gap={1}>{noteValues}</Stack>;
			}
			case 'actions':
				return (
					<Flex gap={2} align="center">
						<Button size="xs" colorPalette="cyan" variant="ghost" onClick={() => setReplyGroupId(group.id)}>
							<VscReply />
							Odpowiedz
						</Button>
						<Button size="xs" colorPalette="red" variant="ghost" onClick={() => setDeleteGroupId(group.id)}>
							<FiTrash2 />
							Usuń
						</Button>
					</Flex>
				);
			default:
				return '';
		}
	};

	if (loading) {
		return (
			<Center my={12}>
				<ProgressCircle.Root value={null}>
					<ProgressCircle.Circle css={{ '--thickness': '12px' }}>
						<ProgressCircle.Track />
						<ProgressCircle.Range stroke="orange.400" />
					</ProgressCircle.Circle>
				</ProgressCircle.Root>
			</Center>
		);
	}

	if (error) {
		console.error(error);
		return null;
	}

	if (!isReady || !reservationsExtended.length) {
		return <Center my={12}>Brak otwartych rezerwacji.</Center>;
	}

	const sortLabel = (column: SortColumn, label: string) => {
		const isActive = sortColumn === column;
		const arrow = isActive ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';
		return `${label}${arrow}`;
	};

	const activeDeleteGroup = groupedRows.find((group) => group.id === deleteGroupId) ?? null;
	const activeReplyGroup = groupedRows.find((group) => group.id === replyGroupId) ?? null;

	const handleDelete = async () => {
		console.log("HERE")
		if (!activeDeleteGroup) {
			return;
		}

		const reservation = activeDeleteGroup.items[0]?.reservation;
		const slotIds = activeDeleteGroup.items
			.map((item) => item.lesson?.slot)
			.filter(Boolean);

		setDeletePending(true);
		try {
			await reservationDelete(database, {
				reservationUid: reservation?.uid,
				reservationId: reservation?.id ?? reservation?.uid,
				slotIds,
			});
			setDeleteGroupId(null);
		} finally {
			setDeletePending(false);
		}
	};

	return (
		<>
			<Table.ScrollArea>
				<Table.Root size="sm">
					<Table.Header>
						<Table.Row>
							{columns.map((column) => {
								const isSortable = column.sortable && column.key !== 'school' && column.key !== 'level' && column.key !== 'grade' && column.key !== 'notes' && column.key !== 'actions';
								const content = isSortable ? sortLabel(column.key as SortColumn, column.label) : column.label;

								return (
									<Table.ColumnHeader
										key={column.key}
										onClick={() => isSortable && handleSort(column.key as SortColumn)}
										cursor={isSortable ? 'pointer' : 'default'}
										userSelect="none"
										fontWeight='bold'
									>
										{content}
									</Table.ColumnHeader>
								);
							})}
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{groupedRows.map((group) => (
							<Table.Row key={group.id}>
								{columns.map((column) => (
									<Table.Cell key={`${group.id}-${column.key}`}>
										{renderCell(column.key, group)}
									</Table.Cell>
								))}
							</Table.Row>
						))}
					</Table.Body>
				</Table.Root>
			</Table.ScrollArea>

			<ActionConfirmationDialog
				isOpen={Boolean(activeDeleteGroup)}
				onClose={() => setDeleteGroupId(null)}
				onAction={handleDelete}
				isPending={deletePending}
				title="Usuń rezerwację"
				bodyText="Czy na pewno chcesz usunąć tę rezerwację? Zostanie usunięta z listy rezerwacji oraz z powiązanych slotów."
				actionText="Usuń"
				actionColor="red"
			/>

			<ReservationReplyDialog
				isOpen={Boolean(activeReplyGroup)}
				onClose={() => setReplyGroupId(null)}
				group={activeReplyGroup}
			/>
		</>
	);
};

export default ReservationsPage;
